import { BookOpen, ChevronLeft, ChevronRight, Copy, Download, EllipsisVertical, Share, SquarePlus, type LucideIcon } from 'lucide-react';
import { useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import { Button } from '../components/ui/button';
import { buildAppIntentUrl, detectLandingPlatform, isIosSafari } from './installLanding';
import { canPromptInstall, detectInstalledApp, promptInstall, subscribeInstall, wasInstalledNow } from './installPrompt';

/** Un paso con su dibujo y su texto. El dibujo imita lo que se ve en el celular (no es una captura). */
function Step({ n, title, children, illustration }: { n: number; title: string; children?: ReactNode; illustration: ReactNode }) {
  return (
    <li className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#6D57BC] text-sm font-bold text-white">{n}</span>
        <div className="text-left">
          <p className="m-0 text-sm font-semibold text-[#2d2049]">{title}</p>
          {children && <p className="m-0 mt-0.5 text-sm text-[#455375]">{children}</p>}
        </div>
      </div>
      <div aria-hidden className="overflow-hidden rounded-xl bg-[#F3EFFF] p-3">
        {illustration}
      </div>
    </li>
  );
}

/** Resalta el elemento que hay que tocar. */
const ring = 'ring-2 ring-[#6D57BC] ring-offset-2 ring-offset-white';

function SafariToolbar() {
  const Icon = ({ children, active }: { children: ReactNode; active?: boolean }) => (
    <span className={`grid h-9 w-9 place-items-center rounded-lg text-[#6b6b7b] ${active ? `${ring} bg-white text-[#6D57BC]` : ''}`}>{children}</span>
  );
  return (
    <div className="mx-auto flex max-w-[16rem] items-center justify-between rounded-2xl bg-white px-2 py-1.5 shadow-sm">
      <Icon>
        <ChevronLeft size={20} />
      </Icon>
      <Icon>
        <ChevronRight size={20} />
      </Icon>
      <Icon active>
        <Share size={20} />
      </Icon>
      <Icon>
        <BookOpen size={20} />
      </Icon>
      <Icon>
        <Copy size={20} />
      </Icon>
    </div>
  );
}

function ShareSheet() {
  const Row = ({ label, icon: RowIcon, active }: { label: string; icon?: LucideIcon; active?: boolean }) => (
    <div className={`flex items-center justify-between rounded-lg bg-white px-3 py-2 text-sm text-[#2d2049] ${active ? ring : ''}`}>
      <span className={active ? 'font-semibold' : ''}>{label}</span>
      {RowIcon && <RowIcon size={18} />}
    </div>
  );
  return (
    <div className="mx-auto flex max-w-[16rem] flex-col gap-1.5">
      <Row label="Copiar" />
      <Row label="Agregar a inicio" icon={SquarePlus} active />
      <Row label="Agregar marcador" />
    </div>
  );
}

function AddScreen() {
  return (
    <div className="mx-auto max-w-[16rem] rounded-2xl bg-white p-3 shadow-sm">
      <div className="flex items-center justify-between text-sm">
        <span className="text-[#6b6b7b]">Cancelar</span>
        <span className={`rounded-md px-2 py-0.5 font-semibold text-[#6D57BC] ${ring}`}>Agregar</span>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <img src="/icons/icon-192.png" alt="" className="h-12 w-12 rounded-xl" />
        <span className="rounded-md bg-[#F3EFFF] px-2 py-1 text-sm text-[#2d2049]">Psi Platform</span>
      </div>
    </div>
  );
}

function HomeScreenIcon() {
  return (
    <div className="mx-auto flex max-w-[16rem] items-center justify-center gap-5 py-2">
      <div className="flex flex-col items-center gap-1">
        <span className="h-12 w-12 rounded-xl bg-white/70" />
        <span className="h-1.5 w-8 rounded bg-[#d9d3ee]" />
      </div>
      <div className="flex flex-col items-center gap-1">
        <img src="/icons/icon-192.png" alt="" className={`h-12 w-12 rounded-xl ${ring}`} />
        <span className="text-[10px] font-medium text-[#2d2049]">Psi Platform</span>
      </div>
      <div className="flex flex-col items-center gap-1">
        <span className="h-12 w-12 rounded-xl bg-white/70" />
        <span className="h-1.5 w-8 rounded bg-[#d9d3ee]" />
      </div>
    </div>
  );
}

/** Instalación en iPhone: no se puede hacer desde un botón, solo con "Agregar a inicio" del menú compartir de Safari. */
export function IosGuide({ safari, device = 'iPhone' }: { safari: boolean; device?: 'iPhone' | 'iPad' }) {
  return (
    <section aria-labelledby="ios-guide-title" className="flex flex-col gap-3">
      <h2 id="ios-guide-title" className="m-0 text-left text-base font-semibold text-[#2d2049]">
        Cómo instalarla en tu {device}
      </h2>
      {!safari && (
        <p role="note" className="m-0 rounded-xl bg-[#FFF4D6] px-4 py-3 text-left text-sm text-[#6b4e00]">
          Para instalarla tenés que abrir esta página en <strong>Safari</strong>: copiá el link, abrí Safari y pegalo ahí.
        </p>
      )}
      <ol className="m-0 flex list-none flex-col gap-3 p-0">
        <Step n={1} title="Tocá el botón Compartir" illustration={<SafariToolbar />}>
          Está en la barra de abajo de Safari (el cuadrado con la flecha hacia arriba).
        </Step>
        <Step n={2} title="Elegí “Agregar a inicio”" illustration={<ShareSheet />}>
          Deslizá el menú hacia arriba si no lo ves.
        </Step>
        <Step n={3} title="Tocá “Agregar”" illustration={<AddScreen />}>
          Arriba a la derecha.
        </Step>
        <Step n={4} title="Abrí la app desde tu pantalla de inicio" illustration={<HomeScreenIcon />}>
          Al entrar, activá las notificaciones para empezar a usarla.
        </Step>
      </ol>
    </section>
  );
}

/** Instalación manual en Android, para cuando el navegador no ofrece el cuadro de instalación. */
function AndroidManualSteps() {
  return (
    <section aria-labelledby="android-guide-title" className="flex flex-col gap-3 text-left">
      <h2 id="android-guide-title" className="m-0 text-base font-semibold text-[#2d2049]">
        Instalala desde el menú del navegador
      </h2>
      <ol className="m-0 flex list-none flex-col gap-3 p-0">
        <Step
          n={1}
          title="Tocá el menú del navegador"
          illustration={
            <div className="mx-auto flex max-w-[16rem] items-center justify-between rounded-2xl bg-white px-3 py-2 shadow-sm">
              <span className="truncate text-sm text-[#6b6b7b]">psiplatform.psimammoliti.com</span>
              <span className={`grid h-8 w-8 place-items-center rounded-lg text-[#6D57BC] ${ring}`}>
                <EllipsisVertical size={20} />
              </span>
            </div>
          }
        >
          Son los tres puntitos, arriba a la derecha (o abajo, según tu navegador).
        </Step>
        <Step
          n={2}
          title="Elegí “Instalar app”"
          illustration={
            <div className="mx-auto flex max-w-[16rem] flex-col gap-1.5">
              <div className="rounded-lg bg-white px-3 py-2 text-sm text-[#2d2049]">Nueva pestaña</div>
              <div className={`flex items-center justify-between rounded-lg bg-white px-3 py-2 text-sm font-semibold text-[#2d2049] ${ring}`}>
                Instalar app <Download size={18} />
              </div>
            </div>
          }
        >
          En algunos navegadores se llama “Agregar a la pantalla principal”.
        </Step>
        <Step
          n={3}
          title="Confirmá con “Instalar”"
          illustration={
            <div className="mx-auto flex max-w-[16rem] items-center gap-3 rounded-2xl bg-white p-3 shadow-sm">
              <img src="/icons/icon-192.png" alt="" className="h-10 w-10 rounded-xl" />
              <span className="text-sm text-[#2d2049]">Psi Platform</span>
              <span className={`ml-auto rounded-md px-2 py-0.5 text-sm font-semibold text-[#6D57BC] ${ring}`}>Instalar</span>
            </div>
          }
        >
          Después abrila desde el ícono “Psi Platform” de tu pantalla de inicio.
        </Step>
      </ol>
    </section>
  );
}

/**
 * Landing estática que reemplaza a la plataforma web en el navegador de un CELULAR: la plataforma se usa desde la app instalada.
 * Sin router ni datos (se monta antes que la app, ver `src/index.tsx`). Tiene el botón para instalar (en Android abre el cuadro de
 * instalación del navegador si está disponible; si no, las instrucciones del menú; en iPhone una guía visual paso a paso) y, debajo,
 * "Abrir la app" para quien ya la instaló.
 */
export function PwaInstallLanding() {
  const platform = useMemo(() => detectLandingPlatform(), []);
  const iosSafari = useMemo(() => isIosSafari(), []);
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const [alreadyInstalled, setAlreadyInstalled] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [openHint, setOpenHint] = useState(false);
  const guideRef = useRef<HTMLDivElement>(null);

  // Llega el evento de instalación o se instaló durante la visita.
  useEffect(() => subscribeInstall(rerender), []);
  useEffect(() => {
    let cancelled = false;
    void detectInstalledApp().then((installed) => {
      if (!cancelled) setAlreadyInstalled(installed);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const installed = alreadyInstalled || wasInstalledNow();

  const install = async () => {
    if (platform === 'ios') {
      guideRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (canPromptInstall()) {
      const result = await promptInstall();
      if (result === 'unavailable') setShowManual(true);
      return;
    }
    setShowManual(true);
    guideRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  const openApp = () => {
    setOpenHint(true);
    // Android (Chrome): se intenta abrir la app instalada con un intent. No hay garantía: si el celular no la resuelve, el aviso de
    // abajo explica cómo abrirla. iPhone no permite abrir una app web instalada desde un link.
    if (platform === 'android' && /Chrome\//.test(navigator.userAgent) && !/; wv\)/.test(navigator.userAgent)) {
      window.location.href = buildAppIntentUrl(window.location);
    }
  };

  return (
    <main className="flex min-h-[100dvh] flex-col items-center gap-6 bg-[#F3EFFF] px-5 pb-[max(2rem,env(safe-area-inset-bottom))] pt-[max(2.5rem,env(safe-area-inset-top))] text-center font-[Poppins,sans-serif] text-[#2d2049]">
      <img src="/logo.svg" alt="Psi Mammoliti" className="h-20 w-20" />

      <header className="flex max-w-sm flex-col gap-2">
        <h1 className="m-0 text-2xl font-semibold">Instalá la app de Psi Mammoliti</h1>
        <p className="m-0 text-base text-[#455375]">
          Tu plataforma ahora se usa desde la app en tu celular: tu agenda, tus pacientes, Comunidad y las notificaciones de tus sesiones.
        </p>
      </header>

      <div className="flex w-full max-w-sm flex-col gap-3">
        {installed && (
          <p role="status" className="m-0 rounded-xl bg-white px-4 py-3 text-sm text-[#455375] shadow-sm">
            Ya tenés la app instalada: abrila desde el ícono “Psi Platform” de tu pantalla de inicio.
          </p>
        )}

        <Button variant="primary" onClick={install} className="h-12 w-full gap-2 rounded-full text-base font-semibold">
          <Download size={20} aria-hidden />
          Instalar la app
        </Button>

        <Button variant="outline" onClick={openApp} className="h-12 w-full rounded-full text-base font-semibold">
          Abrir la app
        </Button>

        {openHint && (
          <p role="status" className="m-0 rounded-xl bg-white px-4 py-3 text-left text-sm text-[#455375] shadow-sm">
            Si no se abrió sola, buscá el ícono <strong>“Psi Platform”</strong> en la pantalla de inicio de tu celular y abrila desde ahí. Si todavía
            no la instalaste, usá el botón “Instalar la app”.
          </p>
        )}
      </div>

      <div ref={guideRef} className="flex w-full max-w-sm scroll-mt-4 flex-col gap-3">
        {platform === 'ios' ? <IosGuide safari={iosSafari} /> : showManual && <AndroidManualSteps />}
      </div>
    </main>
  );
}
